import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Loader2, AlertCircle, Wallet } from 'lucide-react';
import { formatPrice, convertFromEur } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { useRequestPayout } from '../hooks/use-payouts';
import { PayoutMethodCard } from './payout-method-card';
import { ReservedBalanceNotice } from './reserved-balance-notice';
import type { PayoutDestination } from '../types/payout.types';
import { toast } from 'sonner';

interface PayoutRequestFormProps {
  /**
   * Solde disponible du vendeur, en unités MAJEURES (l'API `/payment/seller/balance`
   * le renvoie ainsi depuis cd237dbb — `seller-balance-units.helper.ts` convertit
   * côté serveur). Ne PAS diviser par 100 ici.
   */
  availableBalance: number;
  currency: string;
  payoutMethods: PayoutDestination[];
  /**
   * Minimum de retrait réel du merchant, en unités MAJEURES
   * (`balance.minPayoutAmount`, déjà converti côté serveur). `undefined` si le
   * serveur a omis le champ (`seller-balance-units.helper.ts` ne l'invente
   * jamais) — dans ce cas le formulaire se bloque plutôt que de traiter
   * « minimum inconnu » comme « minimum de 0 » (un minimum réel existe
   * toujours ; un défaut silencieux mentirait comme l'ancien `= 1000` rendu
   * « 1 000,00 € », ou comme `?? 0` rendu « 0,00 € » et un formulaire vide
   * soumissible).
   */
  minimumAmount: number | undefined;
  /**
   * Part du solde gelée par un retrait en cours (`WalletBalance.reserved`), en
   * unités MAJEURES (Doc 55, Task B1). `undefined`/`0` masquent la ligne.
   */
  reserved?: number;
}

export function PayoutRequestForm({
  availableBalance,
  currency,
  payoutMethods,
  minimumAmount,
  reserved,
}: PayoutRequestFormProps) {
  const { t } = useTranslation();
  const { currency: displayCurrency } = useConfigStore();
  const requestPayout = useRequestPayout();

  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  // Une destination non APPROVED ne peut pas recevoir de retrait (doc 51 Phase 4 :
  // l'approbation dépend de la MFA du vendeur). La présélection au montage doit
  // donc filtrer dessus : elle contourne la porte `onClick` de `PayoutMethodCard`,
  // qui ne protège que la resélection manuelle (doc 55, finding A7).
  const [selectedMethod, setSelectedMethod] = useState<PayoutDestination | null>(
    payoutMethods.find((m) => m.isDefault && m.isVerified && m.approvalStatus === 'APPROVED') ||
      payoutMethods.find((m) => m.isVerified && m.approvalStatus === 'APPROVED') ||
      null
  );

  // `amount` est saisi par l'utilisateur en unités MAJEURES. Le marketplace
  // (`POST /payment/seller/payouts` → `createPspPayout` → `convertAmountToCents`)
  // attend lui aussi des unités MAJEURES et fait la conversion en centimes en
  // interne — voir `RequestPayoutData.amount` dans payout.types.ts. Aucune
  // conversion n'est donc faite ici : un `× 100` avait précédemment fait
  // transmettre 100× le montant demandé (25 € → commande de 2 500 €), et
  // aurait été doublement faux pour les devises zéro-décimale (KMF).
  const amountMajor = parseFloat(amount || '0');
  const isValidAmount =
    minimumAmount !== undefined &&
    amountMajor > 0 &&
    amountMajor >= minimumAmount &&
    amountMajor <= availableBalance;
  const verifiedMethods = payoutMethods.filter((m) => m.isVerified);
  const approvedMethods = verifiedMethods.filter((m) => m.approvalStatus === 'APPROVED');
  const isSelectedMethodApproved = selectedMethod?.approvalStatus === 'APPROVED';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedMethod) {
      toast.error(t('payouts.request.selectMethodError'));
      return;
    }

    // Filet indépendant du `disabled` du bouton : une destination non APPROVED ne
    // doit jamais atteindre `requestPayout`, même présélectionnée au montage.
    if (!isSelectedMethodApproved) {
      toast.error(t('payouts.request.selectMethodError'));
      return;
    }

    if (!isValidAmount) {
      toast.error(t('payouts.request.invalidAmountError'));
      return;
    }

    try {
      await requestPayout.mutateAsync({
        amount: amountMajor,
        currency,
        payoutMethodId: selectedMethod.id,
        description: description || undefined,
      });
      toast.success(t('payouts.request.success'));
      setAmount('');
      setDescription('');
    } catch (error: any) {
      toast.error(error.message || t('payouts.request.error'));
    }
  };

  const handleMaxAmount = () => {
    setAmount(availableBalance.toFixed(2));
  };

  if (verifiedMethods.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowUpRight className="size-5" />
            {t('payouts.request.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="size-4" />
            <AlertDescription>
              {t('payouts.request.noVerifiedMethods')}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Minimum inconnu (champ omis par le serveur) : on ne devine pas — un
  // formulaire qui affiche "Minimum : 0,00 €" ou qui accepterait n'importe
  // quel montant serait pire qu'un formulaire bloqué.
  if (minimumAmount === undefined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowUpRight className="size-5" />
            {t('payouts.request.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertDescription>
              {t('payouts.request.minimumUnavailable')}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowUpRight className="size-5" />
          {t('payouts.request.title')}
        </CardTitle>
        <CardDescription>
          {t('payouts.request.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Available Balance */}
          <div className="p-4 bg-muted rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wallet className="size-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t('payouts.request.availableBalance')}</span>
              </div>
              <span className="font-bold text-lg">
                {formatPrice(convertFromEur(availableBalance, displayCurrency), displayCurrency)}
              </span>
            </div>
            {/* Réservé : visible seulement si un retrait en cours gèle une partie du solde (Doc 55, Task B1). */}
            {typeof reserved === 'number' && reserved > 0 && (
              <ReservedBalanceNotice reserved={reserved} currency={displayCurrency} />
            )}
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">{t('payouts.request.amount')}</Label>
            <div className="relative">
              <Input
                id="amount"
                type="number"
                step="0.01"
                min={minimumAmount.toFixed(2)}
                max={availableBalance.toFixed(2)}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="pr-20"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{currency}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleMaxAmount}
                  className="h-6 px-2 text-xs"
                >
                  MAX
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('payouts.request.minimumAmount')}: {formatPrice(convertFromEur(minimumAmount, displayCurrency), displayCurrency)}
            </p>
          </div>

          {/* Select Payout Method */}
          <div className="space-y-3">
            <Label>{t('payouts.request.selectMethod')}</Label>
            {approvedMethods.length === 0 && (
              // Des destinations vérifiées existent mais AUCUNE n'est APPROVED
              // (toutes PENDING_APPROVAL/REJECTED, doc 51 Phase 4). On ne bloque
              // pas toute la carte (contrairement à `noVerifiedMethods` /
              // `minimumAmount === undefined` ci-dessus) : les cartes ci-dessous
              // montrent déjà, destination par destination, laquelle est en
              // attente ou refusée (badge `PayoutMethodCard`) — un bandeau ici
              // se contente d'expliciter pourquoi la soumission reste bloquée,
              // au lieu de laisser un bouton désactivé sans justification
              // visible (« enabled form with nothing selectable »).
              <Alert>
                <AlertCircle className="size-4" />
                <AlertDescription>
                  {t('payouts.request.noApprovedMethods')}
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              {verifiedMethods.map((method) => (
                <PayoutMethodCard
                  key={method.id}
                  method={method}
                  isSelected={selectedMethod?.id === method.id}
                  onSelect={setSelectedMethod}
                  showActions={false}
                />
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('payouts.request.descriptionLabel')} ({t('common.optional')})</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('payouts.request.descriptionPlaceholder')}
              rows={2}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            disabled={!isValidAmount || !selectedMethod || !isSelectedMethodApproved || requestPayout.isPending}
          >
            {requestPayout.isPending ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <ArrowUpRight className="size-4 mr-2" />
                {t('payouts.request.submit')}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
